import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import ky from 'ky'
import { OpenFeature } from '@openfeature/web-sdk'
import { OpenFeatureProvider } from '@openfeature/react-sdk'
import {
  createOfrepInMemoryProvider,
  type OfrepFlag,
} from '@blakeelearning/features/providers/ofrep-in-memory-provider'
import { useStudent } from '../student/hook'

type FeatureFlagFetchResponse = {
  flags?: OfrepFlag[]
}

type FeatureFlagEvaluationContext = {
  targetingKey: string
  customer_type: string
}

async function evaluateFeatureFlags(context: FeatureFlagEvaluationContext) {
  const payload = await ky
    .post('/ofrep/v1/evaluate/flags', {
      credentials: 'include',
      json: { context },
      retry: {
        limit: 3,
        methods: ['POST'],
      },
    })
    .json<FeatureFlagFetchResponse>()

  const flags = Array.isArray(payload.flags) ? payload.flags : []
  OpenFeature.setProvider(createOfrepInMemoryProvider(flags))
}

export default function WithFeatureFlags({ children }: PropsWithChildren) {
  const student = useStudent()
  const [loadedTargetingKey, setLoadedTargetingKey] = useState<string | null>(
    null,
  )

  const evaluationContext = useMemo(
    () => ({
      targetingKey: student.id,
      customer_type: student.customerType.toLowerCase(),
    }),
    [student.id, student.customerType],
  )
  const areFlagsLoaded = loadedTargetingKey === evaluationContext.targetingKey

  useEffect(() => {
    let isCancelled = false

    void evaluateFeatureFlags(evaluationContext)
      .catch(() => {
        // flags default to false on failure
      })
      .finally(() => {
        if (!isCancelled) {
          setLoadedTargetingKey(evaluationContext.targetingKey)
        }
      })

    return () => {
      isCancelled = true
    }
  }, [evaluationContext])

  return areFlagsLoaded ? (
    <OpenFeatureProvider>{children}</OpenFeatureProvider>
  ) : null
}
